import React, { SyntheticEvent } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { PageHeader } from '@routes-plan/ui';
import ShareClientsChoice from '../../../components/offices/ShareClientsChoice';
import { Country } from '../../../models/country';
import { useT } from '../../../i18n';
import { AdminPage, FormActions, FormSection, FullRow } from '../shared/AdminUi';

export interface BusinessModelOption {
  id: number;
  name: string;
  code: string;
}

export interface OfficeFormValues {
  name: string;
  phone: string;
  address: string;
  city: string;
  countryId: string;
  businessModelId: string;
  shareClients: boolean;
}

export type OfficeFormErrors = Partial<Record<keyof OfficeFormValues, string>>;

/** Valida los campos obligatorios de la sede; devuelve los errores por campo. */
export const validateOffice = (v: OfficeFormValues, required: string, typeRequired: string): OfficeFormErrors => {
  const e: OfficeFormErrors = {};
  if (!v.name.trim()) e.name = required;
  if (!v.phone.trim()) e.phone = required;
  if (!v.address.trim()) e.address = required;
  if (!v.city.trim()) e.city = required;
  if (!v.businessModelId) e.businessModelId = typeRequired;
  return e;
};

interface OfficeFormProps {
  mode: 'new' | 'edit';
  values: OfficeFormValues;
  errors: OfficeFormErrors;
  countries: Country[];
  businessModels: BusinessModelOption[];
  onChange: (patch: Partial<OfficeFormValues>) => void;
  onSubmit: () => void;
  saving: boolean;
  allowed: boolean;
  dirty: boolean;
}

/** Formulario de sede (crear y editar): datos de la sede y si sus rutas comparten clientes. */
const OfficeForm: React.FC<OfficeFormProps> = ({
  mode,
  values,
  errors,
  countries,
  businessModels,
  onChange,
  onSubmit,
  saving,
  allowed,
  dirty,
}) => {
  const t = useT();
  const title = mode === 'new' ? t('offices.newTitle') : t('offices.editTitle');
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    onSubmit();
  };
  const field = (key: keyof OfficeFormValues) => ({
    id: `office-${key}`,
    value: values[key] as string,
    error: !!errors[key],
    helperText: errors[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange({ [key]: e.target.value } as Partial<OfficeFormValues>),
    fullWidth: true,
  });

  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={t('offices.formSubtitle')}
        breadcrumbs={[{ label: t('offices.title'), href: '#/admin/offices' }, { label: title }]}
      />
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormSection title={t('offices.sectionData')}>
          <TextField
            {...field('name')}
            label={t('offices.name')}
            placeholder={t('offices.namePlaceholder')}
            inputProps={{ maxLength: 30 }}
            required
          />
          <TextField
            {...field('businessModelId')}
            select
            label={t('offices.officeType')}
            required
          >
            {businessModels.map((m) => (
              <MenuItem key={m.id} value={String(m.id)}>
                {m.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            {...field('phone')}
            type="tel"
            label={t('common.phone')}
            placeholder="6021234567"
            inputProps={{ maxLength: 10 }}
            required
          />
          <TextField {...field('countryId')} select label={t('offices.country')}>
            {countries.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            {...field('city')}
            label={t('offices.city')}
            placeholder={t('offices.cityPlaceholder')}
            inputProps={{ maxLength: 30 }}
            required
          />
          <TextField
            {...field('address')}
            label={t('common.address')}
            placeholder={t('offices.addressPlaceholder')}
            inputProps={{ maxLength: 30 }}
            required
          />
        </FormSection>
        <FormSection title={t('offices.sectionClients')} columns={1}>
          <FullRow>
            <ShareClientsChoice value={values.shareClients} onChange={(v) => onChange({ shareClients: v })} />
          </FullRow>
        </FormSection>
        <FormActions
          cancelTo="/admin/offices"
          saving={saving}
          allowed={allowed}
          deniedTooltip={mode === 'new' ? t('common.noPermissionCreate') : t('common.noPermissionEdit')}
          disabled={mode === 'edit' && !dirty}
        />
      </Box>
    </AdminPage>
  );
};

export default OfficeForm;
